<template>
  <validation-observer ref="simpleRules">
    <b-form>

      <!-- Page Icon -->
      <b-form-group
        :label="$t('Page Icon')"
        label-for="mc-page-icon"
      >
        <b-card
          class="icon-card cursor-pointer text-center mb-2 mx-50"
          @click="selectIcon"
        >
          <div class="icon-wrapper">
            <feather-icon
              :icon="item.icon"
              size="24"
            />
          </div>
          <b-card-text class="icon-name text-truncate mb-0 mt-1">
            {{ item.icon }}
          </b-card-text>
        </b-card>

      </b-form-group>

      <icon-selector
        @selected="setIcon"
      />

      <!-- Page Title -->
      <b-form-group
        :label="$t('Page Title')"
        label-for="mc-page-title"
      >
        <validation-provider
          #default="{ errors }"
          :name="$t('Page Title')"
          rules="required"
        >
          <b-form-input
            v-model="item.title"
            :state="errors.length > 0 ? false:null"
            :placeholder="$t('Please enter...')"
          />
          <small class="text-danger">{{ $t(errors[0]) }}</small>
        </validation-provider>
      </b-form-group>

      <!-- Parent -->
      <b-form-group
        :label="$t('Parent Page')"
        label-for="mc-parent"
      >
        <v-select
          id="vue-select"
          v-model="item.parentId"
          :options="optionPage"
          :reduce="page => page.id"
          label="title"
          :placeholder="$t('Select...')"
        />
      </b-form-group>

      <!-- Description -->
      <b-form-group
        :label="$t('Description')"
        label-for="mc-description"
      >
        <b-form-textarea
          v-model="item.description"
          :label="$t('Please enter...')"
        />

      </b-form-group>

      <!-- Order Number -->
      <b-form-group
        :label="$t('Order Number')"
        label-for="orderNumber"
      >
        <validation-provider
          #default="{ errors }"
          :name="$t('Order Number')"
          rules="required|between:0,999"
        >
          <b-form-input
            v-model="item.orderNumber"
            :state="errors.length > 0 ? false:null"
            placeholder="(0-999)"
          />
          <small class="text-danger">{{ $t(errors[0]) }}</small>
        </validation-provider>
      </b-form-group>

      <!-- User Groups -->
      <b-form-group
        :label="$t('User Groups')"
        label-for="mc-user-groups"
      >
        <v-select
          id="vue-select-user-groups"
          v-model="item.userGroups"
          :options="optionUserGroup"
          :reduce="group => group.id"
          label="groupName"
          :placeholder="$t('Select...')"
          multiple
        />
      </b-form-group>

      <!-- PDF Report -->
      <p><strong class="text-primary">{{ $t('PDF Report') }}</strong></p>
      <b-row>
        <b-col>
          <!-- PDF User Groups -->
          <b-form-group
            :label="$t('PDF User Groups')"
            label-for="mc-pdf-user-groups"
          >
            <v-select
              id="vue-select-pdf-user-groups"
              v-model="item.pdfUserGroups"
              :options="optionUserGroup"
              :reduce="group => group.id"
              label="groupName"
              :placeholder="$t('Select...')"
              multiple
            />
          </b-form-group>
        </b-col>
        <b-col v-if="item.pdfUserGroups && item.pdfUserGroups.length">
          <!-- PDF Day -->
          <b-form-group
            :label="$t('Day')"
            label-for="pdfDay"
          >
            <validation-provider
              #default="{ errors }"
              name="pdfDay"
              rules="required|between:1,31"
            >
              <b-form-input
                v-model="item.pdfDay"
                :state="errors.length > 0 ? false:null"
                placeholder="(1-31)"
              />
              <small class="text-danger">{{ $t(errors[0]) }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <b-col v-if="item.pdfUserGroups && item.pdfUserGroups.length">
          <!-- PDF Filter -->
          <b-form-group
            :label="$t('Apply User Filter')"
            label-for="mc-pdfFilter"
          >
            <b-form-checkbox
              id="checkbox-pdfFilter-page"
              v-model="item.pdfFilter"
              class="mt-1"
              name="checkbox-pdfFilter-page"
              switch
            >
              <span v-if="item.pdfFilter">{{ $t('Yes') }}</span>
              <span v-if="!item.pdfFilter">{{ $t('No') }}</span>
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <template v-if="item.pdfUserGroups && item.pdfUserGroups.length">
        <b-row>
          <b-col>
            <!-- PDF Subject -->
            <b-form-group
              :label="$t('PDF Subject')"
              label-for="pdfSubject"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('PDF Subject')"
                rules="required"
              >
                <b-form-input
                  v-model="item.pdfSubject"
                  :state="errors.length > 0 ? false:null"
                  :placeholder="$t('Please enter...')"
                />
                <small class="text-danger">{{ $t(errors[0]) }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- PdfSubjectIncludePeriod -->
          <b-col>
            <b-form-group
              :label="$t('Include Period')"
              label-for="mc-pdfSubjectIncludePeriod"
            >
              <b-form-checkbox
                id="checkbox-pdfSubjectIncludePeriod-page"
                v-model="item.pdfSubjectIncludePeriod"
                name="checkbox-pdfSubjectIncludePeriod-page"
                switch
              >
                <span v-if="item.pdfSubjectIncludePeriod">{{ $t('Yes') }}</span>
                <span v-if="!item.pdfSubjectIncludePeriod">{{ $t('No') }}</span>
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- PDF Body -->
        <b-form-group
          :label="$t('PDF Body')"
          label-for="pdfBody"
        >
          <b-form-textarea
            v-model="item.pdfBody"
            :placeholder="$t('Please enter...')"
          />
        </b-form-group>
      </template>

      <!-- Excel Attachments -->
      <template v-if="item.pdfUserGroups && item.pdfUserGroups.length">
        <p><strong class="text-secondary">{{ $t('Excel Attachments') }}</strong></p>
        <b-row>
          <!-- Page Select-->
          <b-col md="5">
            <b-form-group
              :label="$t('Page')"
              label-for="mc-parent"
            >
              <v-select
                id="vue-select-page"
                v-model="attachmentPageId"
                :options="optionPageAttachment"
                :reduce="page => page.id"
                label="title"
                :clearable="true"
                :placeholder="$t('Select page...')"
                @input="getBoxOptions(true)"
              />
            </b-form-group>
          </b-col>

          <!-- Box Select-->
          <b-col md="5">
            <b-form-group
              :label="$t('Box')"
              label-for="mc-box"
            >
              <v-select
                id="vue-select"
                v-model="attachmentBoxId"
                :options="optionBox"
                :clearable="true"
                :reduce="box => box.value"
                label="label"
                :placeholder="$t('Select box...')"
              />
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-button
              :disabled="!attachmentPageId || !attachmentBoxId"
              class="mt-2"
              variant="primary"
              @click="addBox"
            >
              {{ $t('Add') }}
            </b-button>
          </b-col>
        </b-row>
        <ul>
          <li
            v-for="(attachment, index) in item.pdfAttachments"
            :key="index"
          >
            {{ attachment.pageTitle }} / {{ attachment.boxTitle }} <b-link @click="item.pdfAttachments.splice(index, 1)">
              <strong><feather-icon icon="TrashIcon" /></strong>
            </b-link>
          </li>
        </ul>
      </template>

      <p><strong class="text-secondary">{{ $t('AI') }}</strong></p>

      <!-- AIActive -->
      <b-form-group
        :label="$t('AI Active')"
        label-for="mc-aiActive"
      >
        <b-form-checkbox
          id="checkbox-aiActive-page"
          v-model="item.aiActive"
          name="checkbox-aiActive-page"
          switch
        >
          <span v-if="item.aiActive">{{ $t('Active') }}</span>
          <span v-if="!item.aiActive">{{ $t('Passive') }}</span>
        </b-form-checkbox>
      </b-form-group>

      <!-- AI Description -->
      <b-form-group v-if="item.aiActive"
        :label="$t('AI Description')"
        label-for="mc-aidescription"
      >
        <b-form-textarea
          v-model="item.aiDescription"
          :placeholder="$t('AI Description')"
        />

      </b-form-group>

      <!-- Status -->
      <b-form-group
        :label="$t('Status')"
        label-for="mc-status"
      >
        <b-form-checkbox
          id="checkbox-status-page"
          v-model="item.status"
          name="checkbox-status-page"
          switch
        >
          <span v-if="item.status">{{ $t('Active') }}</span>
          <span v-if="!item.status">{{ $t('Passive') }}</span>
        </b-form-checkbox>
      </b-form-group>

    </b-form>
  </validation-observer>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'

import {
  BFormGroup, BFormInput, BForm, BFormCheckbox, BFormTextarea, BCard, BCardText, BRow, BCol, BButton, BLink,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import IconSelector from '@/layouts/components/IconSelector.vue'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BFormGroup,
    BFormInput,
    BForm,
    BFormCheckbox,
    BFormTextarea,
    vSelect,
    IconSelector,
    BCard,
    BCardText,
    BRow,
    BCol,
    BButton,
    BLink,
  },
  directives: {
    Ripple,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    parentId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      defaultItem: {
        title: null,
        status: true,
        userGroups: [],
        pdfUserGroups: [],
        icon: 'CircleIcon',
        pdfDay: 4,
        pdfSubject: null,
        pdfBody: null,
        pdfAttachments: [],
      },
      required,
      optionPage: [],
      optionPageAttachment: [],
      optionBox: [],
      optionUserGroup: [],
      attachmentPageId: null,
      attachmentBoxId: null,
    }
  },
  computed: {
    calcOrder() {
      return 1
    },
    item: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        if (this.value) {
          return this.value
        }
        return this.defaultItem
      },
    },
  },
  async created() {
    this.getPageOptions()
    this.getPageOptionsForAttachments()
    this.getUserGroupOptions()
    if (this.parentId) {
      this.item.parentId = this.parentId
    }

    // calculate order number
    const items = this.$store.state.verticalMenu.menuLinks
    if (!this.item.orderNumber) {
      if (!this.parentId) {
        this.item.orderNumber = items.length + 1
      } else {
        this.item.orderNumber = 1
        items.forEach(element => {
          if (element.id === this.parentId) {
            this.item.orderNumber = element.children ? element.children.length + 1 : 1
          } else if (element.children) {
            element.children.forEach(subElement => {
              if (subElement.id === this.parentId) {
                this.item.orderNumber = subElement.children ? subElement.children.length + 1 : 1
              }
            })
          }
        })
      }
    }
  },
  methods: {
    getPageOptions() {
      useJwt.axiosIns.get('page').then(response => {
        if (this.item.id) {
          this.optionPage = response.data.filter(x => x.id !== this.item.id && x.parentId !== this.item.id)
        } else {
          this.optionPage = response.data
        }
      })
    },
    getPageOptionsForAttachments() {
      const menu = this.$store.state.verticalMenu.menuLinks
      this.optionPageAttachment = []
      menu.forEach(item => {
        if (!item.children.length) {
          this.optionPageAttachment.push({ id: item.id, title: this.$t(item.title) })
        } else {
          item.children.forEach(c => {
            if (!c.children.length) {
              this.optionPageAttachment.push({ id: c.id, title: `${this.$t(item.title)}/${this.$t(c.title)}` })
            } else {
              c.children.forEach(cc => {
                this.optionPageAttachment.push({ id: cc.id, title: `${this.$t(item.title)}/${this.$t(c.title)}/${this.$t(cc.title)}` })
              })
            }
          })
        }
      })
    },
    getBoxOptions(clearSelectedBox = false) {
      if (clearSelectedBox) {
        this.attachmentBoxId = null
      }
      if (!this.attachmentPageId) {
        this.optionBox = []
        return
      }
      useJwt.axiosIns.get(`box/${this.attachmentPageId}`).then(response => {
        this.optionBox = response.data.map(box => ({
          value: box.id,
          label: box.boxTitle,
        }))
      })
    },
    getUserGroupOptions() {
      useJwt.axiosIns.get('usergroup').then(response => {
        this.optionUserGroup = response.data
      })
    },
    selectIcon() {
      this.$bvModal.show('iconSelector')
    },
    setIcon(icon) {
      this.$bvModal.hide('iconSelector')
      this.item.icon = icon
    },
    validationForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          const data = JSON.parse(JSON.stringify(this.item))
          if (data.pdfAttachments?.length) {
            data.pdfAttachments = JSON.stringify(data.pdfAttachments)
          } else {
            data.pdfAttachments = null
          }

          if (data.id) {
            useJwt.axiosIns.patch('page', data)
              .then(response => {
                if (response.data) {
                  this.$emit('submit')
                }
              })
              .catch(e => {
                console.log(e)
              })
          } else {
            useJwt.axiosIns.post('page', data)
              .then(response => {
                if (response.data) {
                  this.$emit('submit')
                  this.$router.push({ name: 'dashboard', params: { id: response.data } })
                }
              })
              .catch(e => {
                console.log(e)
              })
          }
        }
      })
    },
    addBox() {
      if (this.attachmentPageId && this.attachmentBoxId) {
        const selectedPage = this.optionPageAttachment.find(page => page.id === this.attachmentPageId)
        const selectedBox = this.optionBox.find(box => box.value === this.attachmentBoxId)
        if (selectedPage && selectedBox) {
          if (!this.item.pdfAttachments) {
            this.item.pdfAttachments = []
          }
          this.item.pdfAttachments.push({
            pageId: selectedPage.id,
            pageTitle: selectedPage.title,
            boxId: selectedBox.value,
            boxTitle: selectedBox.label,
          })
          this.attachmentPageId = null
          this.attachmentBoxId = null
        }
      }
    },
  },
}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-select.scss';
</style>
